import type { WebLogPayload } from './web-log';

/**
 * Map a global `error` event to a client-log payload, or drop it.
 *
 * Returns `null` for opaque cross-origin "Script error." events: when a script
 * served from another origin without CORS headers throws, the browser strips
 * every detail (no `error` object, empty `filename`, `lineno`/`colno` 0, message
 * just "Script error."). Those carry no diagnostic value, so forwarding them only
 * pollutes the log store — drop them here.
 *
 * We key off the structural signature — a nullish `error` AND an empty `filename`
 * — rather than the exact "Script error." message text, which is browser/locale
 * dependent and far less robust. The `error` check is nullish (not truthy) so a
 * genuinely thrown falsy value (`throw 0`, `throw ''`) is not mistaken for "no
 * error". A same-origin uncaught error always populates `filename` (and usually
 * `error`), so it is never dropped here.
 */
export function buildErrorLog(event: ErrorEvent): WebLogPayload | null {
  const { error, message, lineno, colno, filename } = event;

  if (error == null && !filename) {
    return null;
  }

  return {
    attributes: {
      colno,
      lineno,
      name: error instanceof Error ? error.name : undefined,
      source: filename,
      stack: error instanceof Error ? error.stack : undefined,
    },
    level: 'error',
    message,
  };
}

/**
 * Map a global `unhandledrejection` event to a client-log payload, or drop it.
 *
 * Returns `null` for opaque rejections with a nullish `reason` — a bare
 * `Promise.reject()`, `throw undefined`, or an aborted promise that rejects with
 * no value. These carry no message, name, or stack, and `String(reason)` would
 * forward a useless "undefined"/"null" to the log store. Drop them here, mirroring
 * `buildErrorLog`'s structural "Script error." drop.
 */
export function buildRejectionLog(event: PromiseRejectionEvent): WebLogPayload | null {
  const { reason } = event;

  if (reason == null) {
    return null;
  }

  const message = reason instanceof Error ? reason.message : String(reason);

  return {
    attributes: {
      name: reason instanceof Error ? reason.name : undefined,
      stack: reason instanceof Error ? reason.stack : undefined,
    },
    level: 'error',
    message,
  };
}
